// OfflineIndicator — iter118.
//
// Slim full-width bar that lives at the top of the document, IN the
// natural flow (no `position: fixed`). When the bar is present, every
// page header naturally shifts down by 36px; when it disappears,
// content snaps back to the top. No body padding hacks.
//
// States:
//   • OFFLINE  — red `#8B1A1A` bar, ti-wifi-off + "No internet — your
//                Havana is still here". Appears AFTER 4s of confirmed
//                offline.
//   • ONLINE-AGAIN — green `#14532d` bar, ti-wifi + "Back online".
//                    Fades opacity 1→0 over 500ms after a 2s hold,
//                    then unmounts.
//   • HIDDEN   — nothing rendered, zero height.
//
// Mount point: <App /> renders this as its first child so it always
// sits above the page header.
import { useEffect, useRef, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { useI18n } from "../lib/i18n";

const OFFLINE_DEBOUNCE_MS = 4000;
const BACK_ONLINE_HOLD_MS = 2000;
const FADE_MS = 500;

export function OfflineIndicator() {
  const { lang } = useI18n();
  // mode: "hidden" | "offline" | "back-online" | "fading-out"
  const [mode, setMode] = useState("hidden");
  // entrance animation: false → true triggers the 300ms slide.
  const [entered, setEntered] = useState(false);
  const offlineTimer = useRef(null);
  const onlineHoldTimer = useRef(null);
  const fadeTimer = useRef(null);
  const wasOffline = useRef(false);

  // Whenever the mode changes to a visible state, trigger the entrance
  // animation on the next frame so the browser captures the transition
  // from translateY(-36px) → translateY(0).
  useEffect(() => {
    if (mode === "hidden") {
      setEntered(false);
      return;
    }
    if (mode === "offline") {
      requestAnimationFrame(() => setEntered(true));
    } else if (mode === "back-online" || mode === "fading-out") {
      setEntered(true);
    }
  }, [mode]);

  useEffect(() => {
    const clearOffline = () => { if (offlineTimer.current) { clearTimeout(offlineTimer.current); offlineTimer.current = null; } };
    const clearHold = () => { if (onlineHoldTimer.current) { clearTimeout(onlineHoldTimer.current); onlineHoldTimer.current = null; } };
    const clearFade = () => { if (fadeTimer.current) { clearTimeout(fadeTimer.current); fadeTimer.current = null; } };

    const onOffline = () => {
      clearHold(); clearFade();
      clearOffline();
      offlineTimer.current = setTimeout(() => {
        if (!navigator.onLine) {
          wasOffline.current = true;
          setMode("offline");
        }
      }, OFFLINE_DEBOUNCE_MS);
    };

    const onOnline = () => {
      clearOffline();
      if (wasOffline.current) {
        setMode("back-online");
        clearHold();
        onlineHoldTimer.current = setTimeout(() => {
          setMode("fading-out");
          clearFade();
          fadeTimer.current = setTimeout(() => {
            setMode("hidden");
            wasOffline.current = false;
          }, FADE_MS);
        }, BACK_ONLINE_HOLD_MS);
      } else {
        setMode("hidden");
      }
    };

    if (typeof navigator !== "undefined" && !navigator.onLine) onOffline();

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearOffline(); clearHold(); clearFade();
    };
  }, []);

  if (mode === "hidden") return null;

  const isOfflineState = mode === "offline";
  const text = isOfflineState
    ? (lang === "es" ? "Sin internet — tu Habana sigue aquí" : "No internet — your Havana is still here")
    : (lang === "es" ? "Conexión restaurada" : "Back online");
  const Icon = isOfflineState ? WifiOff : Wifi;

  return (
    <div
      data-testid={isOfflineState ? "offline-indicator-offline" : "offline-indicator-back-online"}
      role="status"
      aria-live="polite"
      style={{
        width: "100%",
        height: 36,
        background: isOfflineState ? "#8B1A1A" : "#14532d",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        opacity: mode === "fading-out" ? 0 : 1,
        transform: isOfflineState
          ? (entered ? "translateY(0)" : "translateY(-36px)")
          : "translateY(0)",
        transition: isOfflineState
          ? "transform 300ms ease"
          : `opacity ${FADE_MS}ms ease`,
      }}
    >
      <Icon style={{ width: 13, height: 13, color: "#fff" }} strokeWidth={2} />
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500, color: "#fff" }}>
        {text}
      </span>
    </div>
  );
}
